import { forwardRef, type KeyboardEvent, type MutableRefObject, type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';
import { default as Picker, registerLocale } from 'react-datepicker';
import { ru } from 'date-fns/locale/ru';
import { cn } from '@shared/utils';
import { BaseInput } from '../input';

registerLocale('ru', ru);

const DISPLAY_FORMAT = 'dd / MM / yyyy';
const DEFAULT_PLACEHOLDER = 'dd / mm / yyyy';

const assignRef = (ref: Ref<HTMLInputElement> | undefined, node: HTMLInputElement | null) => {
  if (typeof ref === 'function') ref(node);
  else if (ref) (ref as MutableRefObject<HTMLInputElement | null>).current = node;
};

interface DateFieldProps {
  className?: string;
  wrapperClassName?: string;
  inputClassName?: string;
  label?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  externalRef?: Ref<HTMLInputElement>;
  onClick?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
}

const DateField = forwardRef<HTMLInputElement, DateFieldProps>((props, ref) => {
  return (
    <BaseInput
      ref={(node) => {
        assignRef(ref, node);
        assignRef(props.externalRef, node);
      }}
      className={props.className}
      wrapperClassName={props.wrapperClassName}
      inputClassName={props.inputClassName}
      label={
        props.label ? (
          <>
            {props.label}
            {props.required ? ' *' : ''}
          </>
        ) : undefined
      }
      error={props.error}
      readOnly={props.readOnly}
      uneditable={!props.readOnly && !props.disabled}
      disabled={props.disabled}
      placeholder={props.placeholder}
      value={props.value ?? ''}
      onClick={props.onClick}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      onKeyDown={(event) => {
        props.onKeyDown?.(event);
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) event.preventDefault();
      }}
      onPaste={(event) => event.preventDefault()}
    />
  );
});
DateField.displayName = 'DateField';

interface DateInputProps {
  className?: string;
  wrapperClassName?: string;
  inputClassName?: string;
  label?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  value?: Dayjs;
  onChange?: (value?: Dayjs) => void;
  onBlur?: () => void;
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>((props, ref) => {
  const { i18n } = useTranslation();

  return (
    <div className={cn('relative w-full min-w-0 flex-1', props.className)}>
      <Picker
        selected={props.value?.isValid() ? props.value.toDate() : null}
        locale={i18n.language || 'en'}
        dateFormat={DISPLAY_FORMAT}
        placeholderText={props.placeholder ?? DEFAULT_PLACEHOLDER}
        disabled={props.disabled}
        readOnly={props.readOnly}
        required={props.required}
        minDate={props.minDate?.isValid() ? props.minDate.toDate() : undefined}
        maxDate={props.maxDate?.isValid() ? props.maxDate.toDate() : undefined}
        showPopperArrow={false}
        popperPlacement='bottom-start'
        popperClassName='z-[99]'
        popperProps={{ strategy: 'fixed' }}
        onChange={(date) => props.onChange?.(date ? dayjs(date) : undefined)}
        onBlur={props.onBlur}
        customInput={
          <DateField
            externalRef={ref}
            wrapperClassName={props.wrapperClassName}
            inputClassName={props.inputClassName}
            label={props.label}
            error={props.error}
            required={props.required}
          />
        }
      />
    </div>
  );
});
DateInput.displayName = 'DateInput';
